// Retirement benefit calculation engine.
//
// Ported near-verbatim from SCERS_Retirement_Calculator_v6_0_7.html (the
// standalone SME-reviewed calculator). This file intentionally contains ONLY
// the pure calculation functions from that source — no DOM access, no React,
// no MobX. That keeps it independently testable and lets RetirementCalcEngine.test.ts
// assert byte-for-byte parity against the original tool's output for a fixed
// scenario matrix (see engine/__fixtures__).
//
// Do not "clean up" the math or rounding while porting — behavior must match
// the original exactly. Refactor only once parity tests are green, and re-run
// them after.
//
// Functions intentionally NOT ported here (they mutate DOM input elements or
// generate HTML strings, and are reimplemented as React input handlers/JSX in
// the UI layer instead): capDateYear, isIOSDevice, formatMobileDateInput,
// membershipSelectOptions.

import { RetirementReferenceData, TierCfg, defaultReferenceData } from './data';

export type { TierCfg, RetirementReferenceData } from './data';
export { defaultReferenceData } from './data';

// Tier codes come from RetirementReferenceData.tierConfig, which is designed to
// be swappable at runtime (static today, DB-backed later) — so, unlike a
// hardcoded literal union, this can't be a compile-time-checked set of codes.
export type TierCode = string;

export interface ServiceRetirementAssumption {
  effectiveFrom: string;
  effectiveTo: string;
  tier: string;
  minAge: number;
  interest: number;
  memberMortality: string;
  memberSetback: number;
  beneficiaryMortality: string;
  beneficiarySetback: number;
  option1Description: string;
  erfDescription: string;
}

export interface MbaseDetail {
  integratedServiceHours?: number | string;
  nonIntegratedServiceHours?: number | string;
  integratedSickHours?: number | string;
  nonIntegratedSickHours?: number | string;
}

export interface BaseBenefitResult {
  regular: number;
  sick: number;
  total: number;
  basis: number | { integrated: number; nonIntegrated: number };
  raw: number;
  rounded: number;
  ssReduction?: number;
  oldBenefitPct?: number;
  oldSSFactor?: number;
  mbaseDetail?: { iReg: number; nReg: number; iSick: number; nSick: number; integrated: number; nonIntegrated: number };
}

export interface MembershipComponentInput {
  tier: TierCode;
  serviceRaw: string;
  combinedServiceRaw: string;
  comp: number;
  sickHours: number;
  dob: Date;
  ret: Date;
  benDob: Date | null;
  includeBen: boolean;
  includeTA: boolean;
  ssEstimate: number;
  isCurrent: boolean;
  eligibilityOverride?: boolean;
  mbaseDetail?: MbaseDetail | null;
}

export interface MembershipComponentResult {
  tier: TierCode;
  cfg: TierCfg;
  assumption: ServiceRetirementAssumption;
  serviceRaw: string;
  service: number;
  b: BaseBenefitResult;
  age: number;
  erf: number;
  factor: number;
  continuance: number;
  o1: { amount: number; tableValue: number; ax: number; factor: number; employeeBalance: number; assumption: ServiceRetirementAssumption } | null;
  opt2: number | null;
  opt3: number | null;
  benAge: number | null;
  taEligible: boolean;
  liFactor: number | null;
  isCurrent: boolean;
}

export function isPepraTier(tierValue: string): boolean {
  return tierValue === 'M5' || tierValue === 'S4';
}

export function formatEstimateGeneratedTimestamp(date: Date = new Date()): string {
  const dateText = new Intl.DateTimeFormat('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  }).format(date);
  const timeText = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(date);
  return `Generated ${dateText} at ${timeText}`;
}

export function nonspouseSurvivorLimitPctForAgeGap(diff: number, data: RetirementReferenceData = defaultReferenceData): number {
  if (diff <= 10) return 100;
  const capped = Math.min(Math.floor(diff), 44);
  return data.nonspouseSurvivorLimitPct[capped] ?? 52;
}

export function maxNonspouseContinuancePct(memberDob: Date, beneficiaryDob: Date, retDate: Date, data: RetirementReferenceData = defaultReferenceData) {
  // Mirrors the supplied SCERS worksheet: ages are measured as of each person's
  // birthday in the calendar year containing the annuity starting date.
  const retireeAge = retDate.getFullYear() - memberDob.getFullYear();
  const beneficiaryAge = retDate.getFullYear() - beneficiaryDob.getFullYear();
  const ageDifference = retireeAge - beneficiaryAge;
  const stepAPct = nonspouseSurvivorLimitPctForAgeGap(ageDifference, data);
  if (retireeAge >= 70) return { pct: stepAPct, ageDifference, adjustedAgeDifference: null };
  const adjustedAgeDifference = ageDifference - (70 - retireeAge);
  const stepBPct = nonspouseSurvivorLimitPctForAgeGap(adjustedAgeDifference, data);
  return { pct: Math.max(stepAPct, stepBPct), ageDifference, adjustedAgeDifference };
}

export function bankersRound(v: number, digits: number): number {
  const f = 10 ** digits, n = v * f, lo = Math.floor(n), frac = n - lo, eps = 1e-9;
  let r;
  if (Math.abs(frac - .5) < eps) r = (lo % 2 === 0) ? lo : lo + 1;
  else r = Math.round(n);
  return r / f;
}
export function vbRound4(v: number): number { return bankersRound(v, 4); }
export function vbRound2(v: number): number { return bankersRound(v, 2); }
export function roundPublished(v: number, digits: number): number {
  const f = 10 ** digits;
  return Math.round((v + Number.EPSILON) * f) / f;
}

export function parseDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const s = String(v).trim();
  let y: number, m: number, d: number;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    [y, m, d] = s.split('-').map(Number);
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
    [m, d, y] = s.split('/').map(Number);
  } else {
    return null;
  }
  const dt = new Date(y, m - 1, d, 12);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

export function quarterAge(dob: Date | null, ret: Date | null): number | null {
  if (!dob || !ret || ret < dob) return null;
  let years = ret.getFullYear() - dob.getFullYear();
  if (ret.getMonth() < dob.getMonth() || (ret.getMonth() === dob.getMonth() && ret.getDate() < dob.getDate())) years--;
  let months = ret.getMonth() - dob.getMonth();
  if (ret.getDate() < dob.getDate()) months--;
  if (months < 0) months += 12;
  let result = years;
  if (months >= 3 && months <= 5) result += .25;
  else if (months >= 6 && months <= 8) result += .5;
  else if (months >= 9) result += .75;
  return result;
}

export function exactAgeText(dob: Date, ret: Date): string {
  let y = ret.getFullYear() - dob.getFullYear(), m = ret.getMonth() - dob.getMonth(), d = ret.getDate() - dob.getDate();
  if (d < 0) { m--; d += new Date(ret.getFullYear(), ret.getMonth(), 0).getDate(); }
  if (m < 0) { y--; m += 12; }
  return `${y} years, ${m} months, ${d} days`;
}

export function money(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
}
export function n5(v: number): string { return Number(v).toFixed(5); }
export function parseMoneyInput(v: string): number { return Number(String(v).replace(/[^0-9.]/g, '')); }

// Pure string transform (no DOM element touching) — kept here as a small
// formatting helper shared by engine consumers.
export function isoToMobileDate(v: string | null | undefined): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || '')) return v || '';
  const [y, m, d] = (v as string).split('-');
  return `${m}/${d}/${y}`;
}

export function getERF(name: string, age: number, data: RetirementReferenceData = defaultReferenceData): number | null {
  const rows = data.erfTables[name];
  if (!rows || !rows.length) return null;
  if (age < rows[0][0] - 1e-9) return null;
  const capped = Math.min(age, rows[rows.length - 1][0]);
  for (let i = 0; i < rows.length; i++) if (Math.abs(rows[i][0] - capped) < 1e-9) return rows[i][1];
  return null;
}

export function oldPublishedFactors(cfg: TierCfg, erf: number): { benefitPct: number; ssReductionPerYear: number } | null {
  // SCERS' Old Retirement Formulas table publishes rounded benefit percentages
  // and rounded per-year Social Security reduction factors. The live guest
  // calculator benchmark matches the published values rather than an
  // unrounded ERF-derived amount.
  if (cfg.formula === 'miscOld') {
    return {
      benefitPct: roundPublished((erf / 60) * 100, 3),
      ssReductionPerYear: roundPublished((erf * (350 / 3)) / 60, 3),
    };
  }
  if (cfg.formula === 'safetyOld') {
    return {
      benefitPct: roundPublished((erf / 50) * 100, 4),
      ssReductionPerYear: roundPublished((erf * (350 / 3)) / 50, 3),
    };
  }
  return null;
}

export function effectiveFactorPct(cfg: TierCfg, erf: number): number {
  const old = oldPublishedFactors(cfg, erf);
  if (old) return old.benefitPct;
  if (cfg.formula === 'miscIntegrated') return erf / 60 * 100;
  if (cfg.formula === 'safetyIntegrated') return .03 * erf * 100;
  return erf;
}

export function baseBenefit(cfg: TierCfg, erf: number, comp: number, regularYears: number, sickHours: number, data: RetirementReferenceData = defaultReferenceData): BaseBenefitResult {
  const regular = vbRound4(regularYears);

  if (cfg.oldFormula) {
    // Old Formula represents pre-enhancement service. In the supplied live
    // guest-calculator benchmark, current unused sick leave is not added to
    // the Old Formula service amount.
    const old = oldPublishedFactors(cfg, erf)!;
    const sick = 0;
    const total = regular;
    const gross = comp * (old.benefitPct / 100) * total;
    const ssReduction = old.ssReductionPerYear * total;
    let raw = gross - ssReduction;
    raw = Math.min(raw, comp);
    return {
      regular, sick, total, basis: gross, ssReduction,
      oldBenefitPct: old.benefitPct, oldSSFactor: old.ssReductionPerYear,
      raw, rounded: vbRound2(raw),
    };
  }

  const sick = vbRound4(sickHours / data.serviceCreditsPerYear);
  const total = regular + sick;
  let basis: number, raw: number;
  if (cfg.formula === 'miscIntegrated') {
    basis = comp - (350 / 3);
    raw = (basis / 60) * total * erf;
  } else if (cfg.formula === 'safetyIntegrated') {
    basis = comp - 116.67;
    raw = .03 * basis * total * erf;
  } else {
    basis = comp;
    raw = (basis / 100) * total * erf;
  }
  raw = Math.min(raw, comp);
  return { regular, sick, total, basis, raw, rounded: vbRound2(raw) };
}

export function baseBenefitFromMbaseDetail(cfg: TierCfg, erf: number, comp: number, detail: MbaseDetail, data: RetirementReferenceData = defaultReferenceData): BaseBenefitResult {
  const iReg = vbRound4((Number(detail.integratedServiceHours) || 0) / data.serviceCreditsPerYear);
  const nReg = vbRound4((Number(detail.nonIntegratedServiceHours) || 0) / data.serviceCreditsPerYear);
  const iSick = vbRound4((Number(detail.integratedSickHours) || 0) / data.serviceCreditsPerYear);
  const nSick = vbRound4((Number(detail.nonIntegratedSickHours) || 0) / data.serviceCreditsPerYear);
  const integrated = iReg + iSick;
  const nonIntegrated = nReg + nSick;
  const regular = iReg + nReg;
  const sick = iSick + nSick;
  const total = regular + sick;
  let raw: number, basis: number | { integrated: number; nonIntegrated: number };

  if (cfg.oldFormula) {
    // Detailed MBASE splitting has not yet been validated for old-formula allocations.
    return baseBenefit(cfg, erf, comp, regular, 0, data);
  }

  if (cfg.formula === 'miscIntegrated') {
    const integratedBasis = comp - (350 / 3);
    raw = ((integratedBasis / 60) * integrated * erf) + ((comp / 60) * nonIntegrated * erf);
    basis = { integrated: integratedBasis, nonIntegrated: comp };
  } else if (cfg.formula === 'safetyIntegrated') {
    const integratedBasis = comp - 116.67;
    raw = (.03 * integratedBasis * integrated * erf) + (.03 * comp * nonIntegrated * erf);
    basis = { integrated: integratedBasis, nonIntegrated: comp };
  } else if (cfg.formula === 'pepraNonIntegrated') {
    // RAP M5 treatment: PEPRA percentage applies to both service buckets,
    // but integrated service uses the same $116.67 monthly integration offset
    // used by the Miscellaneous integrated formula. Non-integrated service uses
    // full final compensation. This applies only to the detailed MBASE/RAP path;
    // the public guest calculation remains unchanged.
    const integratedBasis = comp - (350 / 3);
    raw = ((integratedBasis / 100) * integrated * erf) + ((comp / 100) * nonIntegrated * erf);
    basis = { integrated: integratedBasis, nonIntegrated: comp };
  } else {
    raw = (comp / 100) * total * erf;
    basis = { integrated: comp, nonIntegrated: comp };
  }
  raw = Math.min(raw, comp);
  return {
    regular, sick, total, basis, raw, rounded: vbRound2(raw),
    mbaseDetail: { iReg, nReg, iSick, nSick, integrated, nonIntegrated },
  };
}

export function tierAssumptionKey(tier: string): string {
  return String(tier || '').replace(/O$/, '');
}

export function isoDateKey(date: Date | null | undefined): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, '0'), d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getServiceRetirementAssumption(tier: string, retirementDate: Date, data: RetirementReferenceData = defaultReferenceData): ServiceRetirementAssumption | null {
  const key = tierAssumptionKey(tier);
  const dateKey = isoDateKey(retirementDate);
  for (const period of data.rapServiceAssumptionPeriods) {
    const [from, to, rows] = period;
    if (dateKey >= from && dateKey <= to) {
      const row = rows.find(r => r[0] === key);
      if (!row) return null;
      return {
        effectiveFrom: from, effectiveTo: to, tier: key,
        minAge: Number(row[1]), interest: Number(row[2]),
        memberMortality: row[3], memberSetback: Number(row[4] || 0),
        beneficiaryMortality: row[5], beneficiarySetback: Number(row[6] || 0),
        option1Description: row[7], erfDescription: row[8],
      };
    }
  }
  return null;
}

// Keyed per reference-data object (not globally) so two different data
// bundles — e.g. a DB-backed set loaded alongside the static default — can
// never collide on the same description|setback cache key.
const mortalitySeriesCacheByData = new WeakMap<RetirementReferenceData, Map<string, number[]>>();

export function mortalitySeries(description: string, setback = 0, data: RetirementReferenceData = defaultReferenceData): number[] {
  let mortalitySeriesCache = mortalitySeriesCacheByData.get(data);
  if (!mortalitySeriesCache) { mortalitySeriesCache = new Map(); mortalitySeriesCacheByData.set(data, mortalitySeriesCache); }
  const cacheKey = `${description}|${setback}`;
  if (mortalitySeriesCache.has(cacheKey)) return mortalitySeriesCache.get(cacheKey)!;
  const source = data.rapMortalityByDescription[description];
  if (!source) throw new Error(`RAP mortality table "${description}" is not available.`);
  const shifted = Array(116).fill(0);
  for (let age = 0; age < 116; age++) {
    const effectiveAge = Math.max(0, Math.min(115, age + Number(setback || 0)));
    shifted[age] = Number(source[effectiveAge] || 0);
  }
  mortalitySeriesCache.set(cacheKey, shifted);
  return shifted;
}

export function buildActuaryTable(memberAge: number, beneAge: number, assumption: ServiceRetirementAssumption, data: RetirementReferenceData = defaultReferenceData) {
  const qx = mortalitySeries(assumption.memberMortality, assumption.memberSetback, data);
  const qy = mortalitySeries(assumption.beneficiaryMortality, assumption.beneficiarySetback, data);
  const interest = Number(assumption.interest);
  const N = 116, lx = Array(N).fill(0), ly = Array(N).fill(0), v = Array(N).fill(0), Dx = Array(N).fill(0), Dy = Array(N).fill(0), Nx = Array(N).fill(0), Ny = Array(N).fill(0);
  lx[0] = 1; ly[0] = 1;
  for (let i = 1; i < N; i++) { lx[i] = lx[i - 1] * (1 - qx[i - 1]); ly[i] = ly[i - 1] * (1 - qy[i - 1]); }
  for (let i = 0; i < N; i++) { v[i] = Math.pow(1 / (1 + interest), i); Dx[i] = lx[i] * v[i]; Dy[i] = ly[i] * v[i]; }
  Nx[N - 1] = Dx[N - 1]; Ny[N - 1] = Dy[N - 1];
  for (let i = N - 2; i >= 0; i--) { Nx[i] = Nx[i + 1] + Dx[i]; Ny[i] = Ny[i + 1] + Dy[i]; }
  function lxySet(offset: number) {
    const l = Array(N).fill(0);
    l[0] = 1;
    for (let i = 1; i < N; i++) {
      let iy = Math.trunc(i + (Math.floor(beneAge) - Math.floor(memberAge)) + offset);
      iy = Math.max(0, Math.min(115, iy));
      l[i] = l[i - 1] * (1 - qx[i - 1]) * (1 - qy[iy]);
    }
    return l;
  }
  const offsets = [-1, 0, -2, -1], dsets: number[][] = [], nsets: number[][] = [];
  for (const off of offsets) {
    const l = lxySet(off), d = l.map((x, i) => x * v[i]), n = Array(N).fill(0);
    n[N - 1] = d[N - 1];
    for (let i = N - 2; i >= 0; i--) n[i] = d[i] + n[i + 1];
    dsets.push(d); nsets.push(n);
  }
  const fm = Math.floor(memberAge), cm = Math.ceil(memberAge), fb = Math.floor(beneAge), cb = Math.ceil(beneAge), mf = memberAge - fm, bf = beneAge - fb;
  const ax0 = Nx[fm] / Dx[fm] - .5, ax1 = Nx[cm] / Dx[cm] - .5, ax = ax0 + (ax1 - ax0) * mf;
  const ay0 = Ny[fb] / Dy[fb] - .5, ay1 = Ny[cb] / Dy[cb] - .5, ay = ay0 + (ay1 - ay0) * bf;
  const ff = nsets[0][fm] / dsets[0][fm] - .5, fc = nsets[1][fm] / dsets[1][fm] - .5, cf = nsets[2][cm] / dsets[2][cm] - .5, cc = nsets[3][cm] / dsets[3][cm] - .5;
  const aFloor = (fc - ff) * bf + ff, aCeil = (cc - cf) * bf + cf, axy = (aCeil - aFloor) * mf + aFloor;
  return { ax, ay, axy };
}

export function memberAx(memberAge: number, assumption: ServiceRetirementAssumption, data: RetirementReferenceData = defaultReferenceData): number {
  const q = mortalitySeries(assumption.memberMortality, assumption.memberSetback, data);
  const interest = Number(assumption.interest);
  const N = 116, lx = Array(N).fill(0), Dx = Array(N).fill(0), Nx = Array(N).fill(0), v = 1 / (1 + interest);
  lx[0] = 1;
  for (let i = 1; i < N; i++) lx[i] = lx[i - 1] * (1 - q[i - 1]);
  for (let i = 0; i < N; i++) Dx[i] = lx[i] * Math.pow(v, i);
  Nx[N - 1] = Dx[N - 1];
  for (let i = N - 2; i >= 0; i--) Nx[i] = Nx[i + 1] + Dx[i];
  const f = Math.floor(memberAge), c = Math.ceil(memberAge), p = memberAge - f;
  const af = Nx[f] / Dx[f] - .5, ac = Nx[c] / Dx[c] - .5;
  return af + (ac - af) * p;
}

export function option1HistoricalRow(age: number, assumption: ServiceRetirementAssumption, data: RetirementReferenceData = defaultReferenceData): readonly [number, number, string, string] | null {
  const rows = data.rapOption1ByDescription[assumption.option1Description];
  if (!rows || !rows.length) return null;
  for (const r of rows) if (Math.abs(Number(r[0]) - age) < 1e-9) return r;
  return null;
}

export function levelIncomeTableValue(ssAge: number, age: number, assumption: ServiceRetirementAssumption, data: RetirementReferenceData = defaultReferenceData): number | null {
  const row = option1HistoricalRow(age, assumption, data);
  if (!row) return null;
  const value = Number(ssAge) === 62 ? row[2] : Number(ssAge) === 65 ? row[3] : null;
  return value === null || value === undefined || value === '' ? null : Number(value);
}

export function option1TableValue(age: number, assumption: ServiceRetirementAssumption, data: RetirementReferenceData = defaultReferenceData): number | null {
  const row = option1HistoricalRow(age, assumption, data);
  return row ? Number(row[1]) : null;
}

export function option1Estimate(unmodifiedRaw: number, memberAge: number, assumption: ServiceRetirementAssumption, employeeBalance = 6000, data: RetirementReferenceData = defaultReferenceData) {
  const tableValue = option1TableValue(memberAge, assumption, data);
  if (tableValue === null) return null;
  const ax = memberAx(memberAge, assumption, data);
  const straightLifeRate = 1 / (12 * ax);
  const cashRefundRate = tableValue / 1000;
  const amount = unmodifiedRaw + employeeBalance * (cashRefundRate - straightLifeRate);
  return { amount: vbRound2(amount), tableValue, ax, factor: amount / unmodifiedRaw, employeeBalance, assumption };
}

export function optionFactors(memberAge: number, beneAge: number, assumption: ServiceRetirementAssumption, data: RetirementReferenceData = defaultReferenceData) {
  const a = buildActuaryTable(memberAge, beneAge, assumption, data), rev = a.ay - a.axy;
  return { ...a, opt2: a.ax / (a.ax + rev), opt3: a.ax / (a.ax + .5 * rev) };
}

export function decimalAtLeast(rawValue: string | number, threshold: number): boolean {
  const raw = String(rawValue).trim();
  if (!/^\d+(?:\.\d+)?$/.test(raw)) return false;

  const [rawInt, fracPart = ''] = raw.split('.');
  const intPart = rawInt.replace(/^0+(?=\d)/, '') || '0';

  const thresholdStr = String(threshold);
  const [tIntRaw, tFrac = ''] = thresholdStr.split('.');
  const tInt = tIntRaw.replace(/^0+(?=\d)/, '') || '0';

  if (intPart.length !== tInt.length) return intPart.length > tInt.length;
  if (intPart !== tInt) return intPart > tInt;

  const maxLen = Math.max(fracPart.length, tFrac.length);
  const a = fracPart.padEnd(maxLen, '0');
  const b = tFrac.padEnd(maxLen, '0');
  return a >= b;
}

export function sumServiceCreditRaw(rawValues: (string | number)[]): string {
  // BigInt(...) calls used instead of `n` literal suffixes — the source runs
  // fine as-is in-browser, but a literal suffix is rejected by TS when the
  // project's compiled target is below ES2020 (see tsconfig.json "target").
  const SCALE = BigInt(100000);
  let total = BigInt(0);
  for (const raw of rawValues) {
    const s = String(raw ?? '').trim();
    if (!/^\d+(?:\.\d{0,5})?$/.test(s)) return String(Number((rawValues as number[]).reduce((a, v) => a + Number(v || 0), 0)));
    const [whole, frac = ''] = s.split('.');
    total += BigInt(whole) * SCALE + BigInt((frac + '00000').slice(0, 5));
  }
  const whole = total / SCALE;
  const frac = String(total % SCALE).padStart(5, '0').replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : String(whole);
}

export function eligibilityMessageFor(ageValue: number, serviceRaw: string, cfgValue: TierCfg, tierValue: string, combinedServiceRaw: string = serviceRaw, data: RetirementReferenceData = defaultReferenceData): string {
  const minAge = cfgValue.minAge;
  const yrs = Number(serviceRaw);
  const combinedYrs = Number(combinedServiceRaw);

  if (ageValue < minAge) {
    return `A retirement estimate cannot be calculated because the retirement age (${ageValue.toFixed(2)}) is below the minimum age (${minAge.toFixed(2)}) for ${(data.tierConfig[tierValue]?.name || tierValue)}.`;
  }

  // V5.0: PEPRA eligibility is five years of service at the tier minimum age.
  // M5 minimum age = 52; S4 minimum age = 50.
  if (isPepraTier(tierValue)) {
    if (!decimalAtLeast(combinedServiceRaw, 5)) {
      return `A retirement estimate cannot be calculated at age ${ageValue.toFixed(2)} with ${combinedYrs.toFixed(5)} combined years of service. ${(data.tierConfig[tierValue]?.name || tierValue)} requires a full 5.00000 years of service at or above its minimum retirement age. Values below 5 are not rounded up for eligibility.`;
    }
    return '';
  }

  if (ageValue >= 70) return '';

  if (ageValue >= 50) {
    if (!decimalAtLeast(combinedServiceRaw, 10)) {
      return `A retirement estimate cannot be calculated at age ${ageValue.toFixed(2)} with ${combinedYrs.toFixed(5)} combined years of service. At age 50 or older, RAP requires a full 10.00000 combined years of service across the member's Plan / Tier memberships unless the member is age 70 or older. Values below 10 are not rounded up for eligibility.`;
    }
    return '';
  }

  if (tierValue.startsWith('M')) {
    if (!decimalAtLeast(serviceRaw, 30)) {
      return `A Miscellaneous retirement estimate cannot be calculated before age 50 with ${yrs.toFixed(5)} years of service. RAP requires a full 30.00000 years of service before age 50, subject to the plan/tier minimum age. Values below 30 are not rounded up for eligibility.`;
    }
    return '';
  }

  if (tierValue.startsWith('S')) {
    if (!decimalAtLeast(serviceRaw, 20)) {
      return `A Safety retirement estimate cannot be calculated before age 50 with ${yrs.toFixed(5)} years of service. A full 20.00000 years of service is required before age 50, subject to the plan/tier minimum age. Values below 20 are not rounded up for eligibility.`;
    }
    return '';
  }

  return '';
}

export interface ProvisionalMembershipInput {
  tier: string;
  serviceRaw: string;
}

export function provisionalEligibilityAssessment(ageValue: number, memberships: ProvisionalMembershipInput[], combinedServiceRaw: string, data: RetirementReferenceData = defaultReferenceData) {
  const hardIssues: string[] = [];
  const overrideableIssues: string[] = [];

  memberships.forEach(m => {
    const cfg = data.tierConfig[m.tier];
    if (!cfg) return;

    if (ageValue < cfg.minAge) {
      hardIssues.push(`${cfg.name} has a minimum retirement age of ${cfg.minAge.toFixed(2)}.`);
      return;
    }

    if (isPepraTier(m.tier)) {
      if (!decimalAtLeast(combinedServiceRaw, 5)) {
        const combinedYrs = Number(combinedServiceRaw);
        hardIssues.push(
          `${cfg.name} requires a full 5.00000 years of service at or above age ${cfg.minAge.toFixed(2)}. The entered memberships total ${combinedYrs.toFixed(5)} years.`,
        );
      }
      return;
    }

    if (ageValue >= 70) return;

    if (ageValue >= 50) {
      if (!decimalAtLeast(combinedServiceRaw, 10)) {
        const combinedYrs = Number(combinedServiceRaw);
        overrideableIssues.push(
          `The entered memberships total ${combinedYrs.toFixed(5)} years of service. A full 10.00000 years of combined service is normally required at age 50 or older unless the member is age 70 or older.`,
        );
      }
      return;
    }

    const msg = eligibilityMessageFor(ageValue, m.serviceRaw, cfg, m.tier, combinedServiceRaw, data);
    if (msg) hardIssues.push(msg);
  });

  return {
    hardIssues: Array.from(new Set(hardIssues)),
    overrideableIssues: Array.from(new Set(overrideableIssues)),
  };
}

export function calculateMembershipComponent({
  tier, serviceRaw, combinedServiceRaw, comp, sickHours, dob, ret, benDob, includeBen, includeTA, ssEstimate, isCurrent,
  eligibilityOverride = false, mbaseDetail = null,
}: MembershipComponentInput, data: RetirementReferenceData = defaultReferenceData): MembershipComponentResult {
  const baseCfg = data.tierConfig[tier];
  if (!baseCfg) throw new Error('A Plan / Tier selection is missing.');
  const assumption = getServiceRetirementAssumption(tier, ret, data);
  if (!assumption) {
    throw new Error(`No RAP service-retirement assumption set is available for ${baseCfg.name} on ${ret.toLocaleDateString('en-US')}.`);
  }
  const cfg: TierCfg = { ...baseCfg, minAge: assumption.minAge, erf: assumption.erfDescription };
  const service = Number(serviceRaw);
  if (!Number.isFinite(service)) throw new Error(`${cfg.name}: Service Credit must be a valid number.`);
  if (service < 0) throw new Error(`${cfg.name}: Service Credit must be zero or greater.`);
  const age = quarterAge(dob, ret)!;

  const earlyEligibilityMessage = eligibilityMessageFor(age, serviceRaw, cfg, tier, combinedServiceRaw, data);
  const canOverrideTenYearRequirement =
    eligibilityOverride &&
    !isPepraTier(tier) &&
    age >= 50 &&
    age < 70 &&
    !decimalAtLeast(combinedServiceRaw, 10);
  if (earlyEligibilityMessage && !canOverrideTenYearRequirement) throw new Error(`${cfg.name}: ${earlyEligibilityMessage}`);
  if (age < cfg.minAge) throw new Error(`The RAP service-retirement assumptions applicable on ${ret.toLocaleDateString('en-US')} for ${cfg.name} have a minimum age of ${cfg.minAge}.`);
  const erf = getERF(cfg.erf, age, data);
  if (erf === null) throw new Error(`No RAP ERF value is available for age ${age.toFixed(2)} in ${cfg.name}.`);

  const appliedSick = isCurrent ? sickHours : 0;
  const b = mbaseDetail
    ? baseBenefitFromMbaseDetail(cfg, erf, comp, mbaseDetail, data)
    : baseBenefit(cfg, erf, comp, service, appliedSick, data);
  const factor = effectiveFactorPct(cfg, erf);
  const hasBeneficiary = includeBen && !!benDob;
  const continuance = vbRound2(b.raw * .60);
  const o1 = option1Estimate(b.raw, age, assumption, 6000, data);
  let opt2: number | null = null, opt3: number | null = null, benAge: number | null = null;
  if (hasBeneficiary) {
    benAge = quarterAge(benDob, ret);
    if (benAge === null || benAge < 0) throw new Error('Beneficiary birth date must be before the planned retirement date.');
    if (benAge < 18) throw new Error('The spouse/domestic-partner birth date entered results in a beneficiary under age 18 at retirement. This public calculator does not model minor-child beneficiary eligibility.');
    const o = optionFactors(age, benAge, assumption, data);
    opt2 = vbRound2(b.raw * o.opt2);
    opt3 = vbRound2(b.raw * o.opt3);
  }
  const taEligible = !isPepraTier(tier);
  const liFactor = (includeTA && taEligible && ssEstimate > 0) ? levelIncomeTableValue(62, age, assumption, data) : null;
  return { tier, cfg, assumption, serviceRaw, service, b, age, erf, factor, continuance, o1, opt2, opt3, benAge, taEligible, liFactor, isCurrent };
}

export function sumMoney(values: (number | null | undefined)[]): number {
  return vbRound2(values.reduce((a: number, v) => a + (Number.isFinite(v) ? (v as number) : 0), 0));
}

export interface CombinedMembershipInput {
  tier: TierCode;
  serviceRaw: string;
  isCurrent: boolean;
  mbaseDetail?: MbaseDetail | null;
  // Regular contribution balance for this membership's Option 1 (MBASE/RAP override only).
  regularContributions?: number | null;
}

export interface CombinedEstimateInput {
  memberships: CombinedMembershipInput[]; // current/most recent membership first
  comp: number;
  sickHours: number;
  dob: Date;
  ret: Date;
  benDob: Date | null;
  includeBen: boolean;
  includeTA?: boolean;
  ssEstimate?: number;
  eligibilityOverride?: boolean;
}

export interface CombinedEstimateResult {
  components: MembershipComponentResult[];
  age: number;
  hasBeneficiary: boolean;
  isInsurable: boolean;
  service: number;
  unmod: number;
  continuance: number | null;
  opt1: number;
  opt2: number | null;
  opt2Pct: number;
  opt2Cont: number | null;
  opt3: number | null;
  opt3Cont: number | null;
  // Temporary Annuity amounts (before / beginning at 62); null when not applicable.
  ta: Record<'unmod' | 'opt1' | 'opt2' | 'opt3', { before: number; after: number } | null> | null;
}

export type TaOptionKey = 'unmod' | 'opt1' | 'opt2' | 'opt3';

// One Social Security estimate is allocated across the TA-eligible (non-PEPRA)
// memberships in proportion to service credit; the last eligible membership
// absorbs the rounding remainder. Mirrors combinedLI() in v6.0.7.
export function temporaryAnnuityFor(components: MembershipComponentResult[], optionKey: TaOptionKey, ssEstimate: number): { before: number; after: number } {
  const eligible = components.filter(c => c.taEligible);
  const eligibleService = eligible.reduce((sum, c) => sum + c.service, 0);
  if (!(eligibleService > 0)) throw new Error('Temporary Annuity requires service credit in at least one eligible legacy Plan / Tier.');
  let before = 0, after = 0, allocatedSoFar = 0;
  components.forEach(c => {
    const base = optionKey === 'unmod' ? c.b.rounded : optionKey === 'opt1' ? c.o1?.amount : optionKey === 'opt2' ? c.opt2 : c.opt3;
    if (base === null || base === undefined || !Number.isFinite(base)) return;
    if (!c.taEligible) { before += base; after += base; return; }
    if (c.liFactor === null) throw new Error(`Temporary Annuity factors are not available for ${c.cfg.name} at this age.`);
    const isLast = eligible.indexOf(c) === eligible.length - 1;
    const allocatedSS = isLast ? vbRound2(ssEstimate - allocatedSoFar) : vbRound2(ssEstimate * (c.service / eligibleService));
    allocatedSoFar = vbRound2(allocatedSoFar + allocatedSS);
    const componentBefore = vbRound2(base + allocatedSS * c.liFactor / 100);
    const componentAfter = vbRound2(componentBefore - allocatedSS);
    if (!(componentBefore > 0 && componentAfter > 0)) {
      throw new Error('Based on the information provided, a Temporary Annuity is not available because the monthly allowance beginning at age 62 would be reduced to $0 or less.');
    }
    before += componentBefore;
    after += componentAfter;
  });
  return { before: vbRound2(before), after: vbRound2(after) };
}

// Calculates every applicable Plan / Tier on its own, then rolls the monthly
// amounts up exactly as the v6.0.7 calculator does (sum of per-membership
// rounded amounts, re-rounded to cents). Eligibility is judged on the combined
// service of all memberships.
export function calculateCombinedEstimate(input: CombinedEstimateInput, data: RetirementReferenceData = defaultReferenceData, relationship = ''): CombinedEstimateResult {
  const { memberships, comp, sickHours, dob, ret, benDob, includeBen } = input;
  if (!memberships.length) throw new Error('A Plan / Tier selection is missing.');
  const combinedServiceRaw = sumServiceCreditRaw(memberships.map(m => m.serviceRaw));
  const eligibilityAge = quarterAge(dob, ret)!;
  const assessment = provisionalEligibilityAssessment(eligibilityAge, memberships, combinedServiceRaw, data);
  if (assessment.hardIssues.length) throw new Error(`Eligibility: ${assessment.hardIssues.join(' ')}`);

  const components = memberships.map(m => {
    const c = calculateMembershipComponent({
      tier: m.tier, serviceRaw: m.serviceRaw, combinedServiceRaw, comp,
      sickHours: m.isCurrent ? sickHours : 0, dob, ret, benDob, includeBen,
      includeTA: !!input.includeTA, ssEstimate: input.ssEstimate ?? 0, isCurrent: m.isCurrent,
      eligibilityOverride: input.eligibilityOverride, mbaseDetail: m.mbaseDetail ?? null,
    }, data);
    if (m.mbaseDetail && m.regularContributions !== undefined && m.regularContributions !== null) {
      c.o1 = option1Estimate(c.b.raw, c.age, c.assumption, m.regularContributions, data);
    }
    return c;
  });

  const hasBeneficiary = includeBen && !!benDob;
  const isInsurable = hasBeneficiary && relationship === 'insurable';
  const opt2Pct = isInsurable ? maxNonspouseContinuancePct(dob, benDob!, ret, data).pct : 100;
  const opt2 = hasBeneficiary ? sumMoney(components.map(c => c.opt2)) : null;
  const age = components[0].age;
  let ta: CombinedEstimateResult['ta'] = null;
  const ss = input.ssEstimate ?? 0;
  if (input.includeTA && ss > 0 && age < 62 && components.some(c => c.taEligible)) {
    ta = {
      unmod: temporaryAnnuityFor(components, 'unmod', ss),
      opt1: temporaryAnnuityFor(components, 'opt1', ss),
      opt2: hasBeneficiary ? temporaryAnnuityFor(components, 'opt2', ss) : null,
      opt3: hasBeneficiary ? temporaryAnnuityFor(components, 'opt3', ss) : null,
    };
  }
  return {
    components,
    age: components[0].age,
    hasBeneficiary,
    isInsurable,
    service: components.reduce((a, c) => a + c.b.total, 0),
    unmod: sumMoney(components.map(c => c.b.rounded)),
    continuance: hasBeneficiary && !isInsurable ? sumMoney(components.map(c => c.continuance)) : null,
    opt1: sumMoney(components.map(c => c.o1?.amount)),
    opt2,
    opt2Pct,
    opt2Cont: opt2 === null ? null : vbRound2(opt2 * opt2Pct / 100),
    opt3: hasBeneficiary ? sumMoney(components.map(c => c.opt3)) : null,
    opt3Cont: hasBeneficiary ? sumMoney(components.map(c => vbRound2((c.opt3 ?? 0) * 0.5))) : null,
    ta,
  };
}

export function friendlyCalculationError(err: unknown): string {
  const raw = String((err as Error)?.message || err || '').trim();

  if (/Plan \/ Tier|Service Credit|Beneficiary|Temporary Annuity|birth date|retirement date|minimum age|ERF|eligibility/i.test(raw)) {
    return raw;
  }
  if (/is not defined/i.test(raw)) {
    return 'The estimate could not be calculated because of an internal calculator error. Please review your entries and try again. If the issue continues, please contact SCERS.';
  }
  if (/Cannot read properties of undefined|Cannot read property/i.test(raw)) {
    return 'The estimate could not be calculated because one of the entered values could not be processed. Please review the Plan / Tier and service credit entries and try again.';
  }
  return 'The estimate could not be calculated. Please review your entries and try again. If the issue continues, please contact SCERS.';
}
